import type { MouseEvent, SyntheticEvent } from 'react'
import { legacyContractDataAttrs, legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import { selectCheckedSubIds } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

function stop(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

export function PortalFoldSubsButton({ section, label, count }: { section: 0 | 1; label: string; count?: number }) {
  return (
    <a
      href="#"
      {...legacyHookAttrs(section === 0 ? 'subs.menuToggle.answers' : 'subs.menuToggle.submsges', 'md-fold-subs inline-flex w-max items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 no-underline hover:bg-blue-100 hover:text-blue-800')}
      onClick={event => {
        stop(event)
        dispatchSubsDisclosure({ action: 'toggle-section', section, target: event.currentTarget })
      }}
    >
      <span>{label}</span>{typeof count === 'number' ? <span className="text-slate-400">({count})</span> : null}
    </a>
  )
}

export function PortalPagingBar({
  section,
  currentPage = 1,
  totalPages = 1,
  registerContract = true,
}: {
  section: 0 | 1
  currentPage?: number
  totalPages?: number
  registerContract?: boolean
}) {
  const paging = createPagingModel(currentPage, totalPages)
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const pageClass = 'md-paging__page inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-semibold text-slate-600 hover:bg-white hover:text-blue-700'

  function pageClick(event: MouseEvent<HTMLElement>, page: PagingTarget) {
    stop(event)
    if (page === 'previous' && paging.previousDisabled) return
    if (page === 'next' && paging.nextDisabled) return
    dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget })
  }

  return (
    <div {...(registerContract ? legacyHookAttrs(contract, 'md-paging inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1') : { className: 'md-paging inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1' })}>
      {paging.tokens.map((token, index) => token === 'gap' ? (
        <span key={`gap-${index}`} className="px-1 text-slate-400">…</span>
      ) : (
        <a key={token} href="#" className={`${pageClass}${token === paging.currentPage ? ' md-paging__page--current bg-blue-600 text-white hover:bg-blue-600 hover:text-white' : ''}`} onClick={event => pageClick(event, token)}>{token}</a>
      ))}
      <span className="md-paging__sequence inline-flex items-center gap-1">
        <a href="#" className={`${pageClass}${paging.previousDisabled ? ' md-hook-disabled opacity-40' : ''}`} style={paging.previousDisabled ? { display: 'none' } : undefined} onClick={event => pageClick(event, 'previous')} aria-label="Previous page">‹</a>
        <a href="#" className={`${pageClass}${paging.nextDisabled ? ' md-hook-disabled opacity-40' : ''}`} style={paging.nextDisabled ? { display: 'none' } : undefined} onClick={event => pageClick(event, 'next')} aria-label="Next page">›</a>
      </span>
    </div>
  )
}

export function PortalRefreshSubs({ section }: { section: 0 | 1 }) {
  return <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700" title="Refresh" onClick={event => dispatchSubsNavigation({ action: 'refresh', section, target: event.currentTarget })}>↻</button>
}

export function PortalCheckedsInfo({ section }: { section: 0 | 1; mode?: 'msg' | 'drafts' | 'trash' | 'favs'; visual?: 'modern' | 'legacy' }) {
  const { state } = useSubsStore()
  const sectionKey = section === 0 ? 'answers' : 'submsges'
  const checkedMode = state.renderProjection.mode === 'subs' ? sectionKey : state.renderProjection.mode
  const checkedIds = selectCheckedSubIds(state, sectionKey, checkedMode)
  const currentIds = state.renderProjection.rootIds[sectionKey] || []
  const checkedSet = new Set(checkedIds)
  const currentCount = currentIds.filter(id => checkedSet.has(id)).length
  const contract = section === 0 ? 'subs.checked.answers' : 'subs.checked.submsges'

  return (
    <div {...legacyContractDataAttrs(contract)} className="inline-flex min-h-8 items-center text-xs text-slate-600">
      <button type="button" className="h-8 rounded-lg bg-slate-100 px-2 hover:bg-blue-100 hover:text-blue-700" title="Select visible" onClick={event => dispatchSubsSelection({ action: 'toggle-visible', section, target: event.currentTarget })}>
        ✓ {currentCount || ''}{checkedIds.length ? ` (${checkedIds.length})` : ''}
      </button>
    </div>
  )
}
